import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { DEFAULT_THEME } from '@tv-casino/shared';
import { FreeDuringAlpha } from '../components/AlphaNotice.js';
import { themeStyle } from '../lib/themeStyle.js';
import {
  ROKU_SHOTS,
  SETUP_STEPS,
  shotSrc,
  shotThumb,
  stepShot,
} from '@tv-casino/viewmodels/routes/rokuLandingModel.js';
import styles from './RokuLanding.module.css';

const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(' ');

/** The capture the hero hangs in its cabinet. */
const HERO = ROKU_SHOTS.find((s) => s.id === 'blackjack-win')!;

/** What the night is, in three lines. */
const HOW = [
  {
    title: 'The Roku is the table',
    body: 'It draws the felt, deals the cards, spins the wheel and throws the dice. The dealer calls the action out loud.',
  },
  {
    title: 'Phones are the controllers',
    body: 'Guests scan the code on the TV and play in their phone’s browser. Nothing to install, no account to make.',
  },
  {
    title: 'Chips follow the player',
    body: 'Each stack belongs to the player, not the table, so you can lose it at blackjack and win it back at craps.',
  },
] as const;

/** A TV cabinet with marquee bulbs around the rim. */
function Cabinet({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx(styles.cabinet, className)}>
      <span className={styles.bulbs} aria-hidden="true" />
      <div className={styles.glass}>{children}</div>
    </div>
  );
}

/** Warm the browser cache for the captures either side of the one on screen. */
function usePreloadNeighbors(index: number) {
  useEffect(() => {
    for (const by of [-1, 1]) {
      const img = new Image();
      img.src = shotSrc(ROKU_SHOTS[stepShot(index, by)]!);
    }
  }, [index]);
}

function Lightbox({
  index,
  onIndex,
  onClose,
}: {
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
}) {
  const shot = ROKU_SHOTS[index]!;
  const closeRef = useRef<HTMLButtonElement>(null);
  usePreloadNeighbors(index);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') onIndex(stepShot(index, 1));
      else if (e.key === 'ArrowLeft') onIndex(stepShot(index, -1));
    };
    window.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [index, onIndex, onClose]);

  return (
    <div
      className={styles.lightbox}
      role="dialog"
      aria-modal="true"
      aria-label={shot.title}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <figure className={styles.lightboxFigure}>
        <img className={styles.lightboxImg} src={shotSrc(shot)} alt={shot.alt} width={1920} height={1080} />
        <figcaption className={styles.lightboxCap}>
          <span className={styles.count}>
            {index + 1} / {ROKU_SHOTS.length}
          </span>
          <b>{shot.title}.</b> {shot.caption}
        </figcaption>
      </figure>
      <button
        type="button"
        className={cx(styles.arrow, styles.lightboxPrev)}
        aria-label="Previous screenshot"
        onClick={() => onIndex(stepShot(index, -1))}
      >
        ‹
      </button>
      <button
        type="button"
        className={cx(styles.arrow, styles.lightboxNext)}
        aria-label="Next screenshot"
        onClick={() => onIndex(stepShot(index, 1))}
      >
        ›
      </button>
      <button ref={closeRef} type="button" className={styles.lightboxClose} aria-label="Close" onClick={onClose}>
        ✕
      </button>
    </div>
  );
}

/**
 * The gallery: one capture large in a cabinet, a filmstrip of the rest under
 * it. Arrow keys walk it while it has focus; clicking the screen opens the
 * capture at full size.
 */
function Gallery() {
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const stageRef = useRef<HTMLButtonElement>(null);
  const shot = ROKU_SHOTS[index]!;
  usePreloadNeighbors(index);

  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (open) return;
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      setIndex((i) => stepShot(i, 1));
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setIndex((i) => stepShot(i, -1));
    }
  };

  const close = useCallback(() => {
    setOpen(false);
    stageRef.current?.focus();
  }, []);

  return (
    <div className={styles.gallery} onKeyDown={onKeyDown}>
      <div className={styles.stageWrap}>
        <Cabinet>
          <button
            ref={stageRef}
            type="button"
            className={styles.stage}
            aria-label={`View ${shot.title} full size`}
            onClick={() => setOpen(true)}
          >
            <img key={shot.id} className={styles.stageImg} src={shotSrc(shot)} alt={shot.alt} width={1920} height={1080} />
            <span className={styles.zoomHint} aria-hidden="true">
              Full size
            </span>
          </button>
        </Cabinet>
        <button
          type="button"
          className={cx(styles.arrow, styles.stagePrev)}
          aria-label="Previous screenshot"
          onClick={() => setIndex(stepShot(index, -1))}
        >
          ‹
        </button>
        <button
          type="button"
          className={cx(styles.arrow, styles.stageNext)}
          aria-label="Next screenshot"
          onClick={() => setIndex(stepShot(index, 1))}
        >
          ›
        </button>
      </div>

      <p className={styles.caption} aria-live="polite">
        <span className={styles.count}>
          {index + 1} / {ROKU_SHOTS.length}
        </span>
        <b>{shot.title}.</b> {shot.caption}
      </p>

      <ul className={styles.strip} aria-label="Screenshots">
        {ROKU_SHOTS.map((s, i) => (
          <li key={s.id}>
            <button
              type="button"
              className={cx(styles.thumb, i === index && styles.thumbOn)}
              aria-label={s.title}
              aria-current={i === index ? 'true' : undefined}
              onClick={() => setIndex(i)}
            >
              <img src={shotThumb(s)} alt="" width={480} height={270} loading="lazy" />
              <span className={styles.thumbLabel}>{s.title}</span>
            </button>
          </li>
        ))}
      </ul>

      {open && <Lightbox index={index} onIndex={setIndex} onClose={close} />}
    </div>
  );
}

/**
 * The Roku page: real captures off the box, what a night looks like, and how
 * to get the channel. It is written as if the channel is in the Roku
 * Streaming Store.
 */
export function RokuLanding() {
  return (
    <main className={styles.page} style={themeStyle(DEFAULT_THEME)}>
      <nav className={styles.topbar}>
        <Link className={styles.back} to="/">
          ← TV Casino Night
        </Link>
      </nav>

      <section className={styles.hero}>
        <div className={styles.pitch}>
          <p className={styles.kicker}>Roku channel</p>
          <h1 className={styles.title}>
            The casino, <span className={styles.neon}>on your Roku</span>
          </h1>
          <p className={styles.sub}>
            Blackjack, roulette, craps, baccarat, hold&rsquo;em and keno on the TV you already own.
            The Roku runs the table. Everyone else plays from their phone.
          </p>
          <div className={styles.actions}>
            <a className={cx(styles.button, styles.primary)} href="#screens">
              See the screens
            </a>
            <a className={styles.button} href="#get">
              Get the channel
            </a>
          </div>
          <p className={styles.facts}>Free during alpha · up to 7 players a table · no app for guests</p>
        </div>

        <figure className={styles.heroFigure}>
          <Cabinet className={styles.heroCabinet}>
            <img
              className={styles.stageImg}
              src={shotSrc(HERO)}
              alt={HERO.alt}
              width={1920}
              height={1080}
            />
          </Cabinet>
          <figcaption className={styles.heroCap}>Screenshot from a Roku Express 4K+</figcaption>
        </figure>
      </section>

      <section className={styles.band} id="screens" aria-labelledby="screens-title">
        <header className={styles.bandHead}>
          <h2 className={styles.bandTitle} id="screens-title">
            Straight off the box
          </h2>
          <p className={styles.bandBody}>
            Unedited 1080p screenshots from one night with three players. Use the arrow keys or the
            strip to flip through, and click the screen for full size.
          </p>
        </header>
        <Gallery />
      </section>

      <section className={styles.band} aria-labelledby="how-title">
        <header className={styles.bandHead}>
          <h2 className={styles.bandTitle} id="how-title">
            The TV deals. The phones decide.
          </h2>
        </header>
        <ul className={styles.how}>
          {HOW.map((item) => (
            <li key={item.title} className={styles.howItem}>
              <h3 className={styles.howTitle}>{item.title}</h3>
              <p className={styles.howBody}>{item.body}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className={styles.band} id="get" aria-labelledby="get-title">
        <header className={styles.bandHead}>
          <h2 className={styles.bandTitle} id="get-title">
            Get the channel
          </h2>
          <p className={styles.bandBody}>
            TV Casino Night is a free channel in the Roku Streaming Store. Guests don&rsquo;t install
            anything.
          </p>
        </header>
        <div className={styles.install}>
          <div className={styles.stepsCol}>
            <ol className={styles.steps}>
              {SETUP_STEPS.map((step, i) => (
                <li key={step.title} className={styles.step}>
                  <span className={styles.stepNum} aria-hidden="true">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className={styles.stepTitle}>{step.title}</h3>
                    <p className={styles.stepBody}>{step.body}</p>
                  </div>
                </li>
              ))}
            </ol>
            <p className={styles.aside}>
              <b>Running a casino?</b> Press Left on the remote, type your casino&rsquo;s code, and pick
              a table. The Roku stays on that table from then on, and every player&rsquo;s chips follow
              them from night to night.
            </p>
          </div>
          <figure className={styles.setupFigure}>
            <Cabinet>
              <img
                className={styles.stageImg}
                src="/roku/lobby.jpg"
                alt="The Roku join screen: a large join code beside a QR code."
                width={1920}
                height={1080}
                loading="lazy"
              />
            </Cabinet>
            <figcaption className={styles.heroCap}>What the room sees: a code and a QR</figcaption>
          </figure>
        </div>
      </section>

      <section className={styles.closer}>
        <h2 className={styles.closerTitle}>Deal the first hand</h2>
        <p className={styles.closerSub}>
          Make a casino, pair the Roku to it, and let the couch scan in.
        </p>
        <div className={styles.actions}>
          <Link className={cx(styles.button, styles.primary)} to="/casino">
            Start a casino
          </Link>
          <Link className={styles.button} to="/play">
            Join with a code
          </Link>
        </div>
      </section>

      <FreeDuringAlpha className={styles.free} />
    </main>
  );
}
